/**
 * PATH: packages/ui/src/lib/undoSendPref.ts
 * FILE_ID: UI-LIB-UNDOSENDPREF-001
 * PURPOSE: The single source of truth for the email "undo send" grace-window
 *          length — how many seconds the composer holds a message after you
 *          hit Send so you can still hit "Undo" (Gmail / Outlook style).
 * WHY:     Operator: "Why is it sending in 10 seconds? … there should be
 *          options to add and customise the delays … or remove the delay."
 *          The undo-send window was a fixed 8s constant with no way to change
 *          or turn it off. This makes it a user preference.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────────┐   ┌────────────────────┐
 *   │ Settings UI  │ → │ setUndoSendSeconds()   │ → │ localStorage        │
 *   │ <select>     │   │ (clamp + persist)      │   │ (device-local pref) │
 *   └──────────────┘   └───────────────────────┘   └────────────────────┘
 *   Composer mount → getUndoSendDelayMs() → useUndoSend(ms) (0 = send now).
 * SOURCE OF TRUTH = the backend (`undo_send_seconds` in the per-user+org email
 *   preferences, `/api/settings/email/`), so the choice follows the user across
 *   devices. This localStorage value is a SYNCHRONOUS CACHE/MIRROR of that DB
 *   value — the undo-send hold is created at composer mount and needs the window
 *   length immediately (no time to await a fetch). EmailTab writes the cache
 *   whenever prefs load/save; the composer self-heals it via a background fetch.
 * RELATED:
 *   - packages/email-hub/.../compose/sendHold.ts (the hold; DEFAULT was 8000)
 *   - packages/email-hub/.../compose/useUndoSend.ts (accepts windowMs)
 *   - packages/email-hub/.../pages/ComposePage.tsx (reads this, arms the hold)
 *   - packages/ui/.../settings/EmailTab.tsx (the picker)
 */

/** localStorage key for the device-local undo-send window (in seconds). */
export const UNDO_SEND_STORAGE_KEY = 'quotech.undoSendDelaySeconds'

/** Default grace window when the operator has never chosen one (seconds). */
export const DEFAULT_UNDO_SEND_SECONDS = 10

/** Upper bound so a corrupt/hand-edited value can't hold a send forever. */
const MAX_UNDO_SEND_SECONDS = 120

/** The discrete choices offered in the settings picker (Off + Gmail-style). */
export const UNDO_SEND_DELAY_OPTIONS: ReadonlyArray<{ seconds: number; label: string }> = [
  { seconds: 0, label: 'Off — send immediately' },
  { seconds: 5, label: '5 seconds' },
  { seconds: 10, label: '10 seconds' },
  { seconds: 20, label: '20 seconds' },
  { seconds: 30, label: '30 seconds' },
]

/** Resolve a usable Storage, or null when unavailable (SSR / private mode). */
function safeStorage(): Storage | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/**
 * Pure: coerce any stored/raw value to a valid second count. Non-finite,
 * negative, or absent → the default; anything else is rounded and clamped to
 * [0, MAX]. Exported for direct unit testing without touching storage.
 */
export function normaliseUndoSendSeconds(raw: unknown): number {
  // Treat absent / blank explicitly — `Number(null)` and `Number('')` are 0,
  // which would otherwise be mistaken for a deliberate "Off".
  if (raw == null || raw === '') return DEFAULT_UNDO_SEND_SECONDS
  const n = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(n) || n < 0) return DEFAULT_UNDO_SEND_SECONDS
  return Math.min(MAX_UNDO_SEND_SECONDS, Math.round(n))
}

/** Read the configured undo-send window in SECONDS (default when unset). */
export function getUndoSendSeconds(storage: Storage | null = safeStorage()): number {
  if (!storage) return DEFAULT_UNDO_SEND_SECONDS
  try {
    const raw = storage.getItem(UNDO_SEND_STORAGE_KEY)
    if (raw == null) return DEFAULT_UNDO_SEND_SECONDS
    return normaliseUndoSendSeconds(raw)
  } catch {
    return DEFAULT_UNDO_SEND_SECONDS
  }
}

/** Read the configured undo-send window in MILLISECONDS (0 = off). */
export function getUndoSendDelayMs(storage: Storage | null = safeStorage()): number {
  return getUndoSendSeconds(storage) * 1000
}

/** Persist the undo-send window (seconds). No-op when storage is unavailable. */
export function setUndoSendSeconds(seconds: number, storage: Storage | null = safeStorage()): void {
  if (!storage) return
  try {
    storage.setItem(UNDO_SEND_STORAGE_KEY, String(normaliseUndoSendSeconds(seconds)))
  } catch {
    /* quota / private mode — preference simply doesn't persist */
  }
}
